import { allowBillingNavigation } from "./billing-leave-guard";
import React, { useContext, useEffect, useRef, useState } from "react";
import { DataContext } from "../layouts/main-layout";
import {
  DiscountValueInput,
  changedItemEdits,
  previewBillTotal,
} from "./bill-discount-edit";
import AddButton from "../buttons/button";
import ItemsInputField from "../forms/items-input-field";
import ItemsSelectField from "../forms/items-select-field";
import UploadIcon from "../../assets/icons/upload-icon";
import ViewIcon from "../../assets/icons/view-icon";
import { useFormikContext } from "formik";
import { ChevronUp, ChevronDown } from "lucide-react";
import { handleShowErrorToast } from "../helper-components/helper-function";
import BillingUploadPrescription from "./billing-upload-prescription";
import { useNavigate } from "react-router-dom";
import errorToast from "../helper-components/error-toast";
import SearchField from "../forms/search-field";
import { doctorTableAction } from "../../redux/actions/doctor-action";
import { useDispatch } from "react-redux";
import TextSearchField from "../forms/text-select-field";
import {
  invoiceDiscountUpdateAction,
  invoiceDraftCreateAction,
  invoiceEditAction,
} from "../../redux/actions/invoices-action";
import successToast from "../helper-components/success-toast";
import LoadingOverlay from "../helper-components/overlay-loading";
import moment from "moment";
import {
  headerDiscountWithinLimits,
  headerLimitMessage,
  itemDiscountLimits,
} from "./item-discount-limits";

const BillingFooterSection = ({
  docList = [],
  disabled,
  page = "create",
  billCreateInputs = [],
  user_discount = {},
  formValuesRef,
  handleBilling,
  searchRef,
  storeId,
  clickDraftRef,
  submitLoading = false,
  onBillUpdated = () => {},
}) => {
  const header_inv_dis_per_ref = useRef();
  const { values, setFieldValue, setFieldError, errors, validateForm } =
    useFormikContext();
  console.log(values, "dsfdsfsdfsdfsdfsdf");
  const [isOpen, setIsOpen] = useState(false);
  const [uploadPrescription, setUploadPrescription] = useState(false);
  const [uploadPrescriptionView, setUploadPrescriptionView] = useState(false);
  const [localData, setLocalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flag,setFlag]=useState(true);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { permissionFn } = useContext(DataContext) || {};

  // A finished bill's invoice discount can be changed on its own from the view
  // page, by users with the billing "Edit Discount" permission, for as long as
  // the backend allows (can_edit_discount: within ruleBook's editDiscountDays)
  const canEditDiscount =
    page === "view" &&
    !!values?.sot_id &&
    values?.can_edit_discount === true &&
    !!permissionFn?.("invoice")?.edit_discount;

  // Editing in place: the invoice discount here, item discounts in the items
  // table (bill-discount-edit.js); both are saved together and the bill reloaded
  const isEditingDiscount = canEditDiscount && !!values?.discount_edit;
  const invoiceDiscountEdit = values?.discount_edit?.invoice;

  const startDiscountEdit = () => {
    setFieldValue("discount_edit", {
      invoice: {
        value: values?.header_inv_dis_per ?? 0,
        isPercentage: !!values?.sot_is_invoice_dis_percentage,
      },
      items: {},
    });
  };

  const setInvoiceDiscountEdit = (change) => {
    setFieldValue("discount_edit", {
      ...values.discount_edit,
      invoice: { ...invoiceDiscountEdit, ...change },
    });
  };

  const saveDiscountEdit = () => {
    const invoiceValue = parseFloat(invoiceDiscountEdit?.value || 0);
    if (!(invoiceValue >= 0) || (invoiceDiscountEdit?.isPercentage && invoiceValue > 100)) {
      errorToast("Enter an invoice discount between 0 and 100%, or a ₹ amount of 0 or more");
      return;
    }
    setSavingDiscount(true);
    dispatch(
      invoiceDiscountUpdateAction(
        {
          sot_id: values?.sot_id,
          percentage: invoiceValue,
          isPercentage: !!invoiceDiscountEdit?.isPercentage,
          items: changedItemEdits(values?.discount_edit?.items),
        },
        (data) => {
          setSavingDiscount(false);
          successToast(data?.message);
          setFieldValue("discount_edit", null);
          onBillUpdated();
        },
        (data) => {
          setSavingDiscount(false);
          errorToast(data?.data?.message || "Could not update the discounts");
        }
      )
    );
  };

  // Auto-recalculate totals when data changes. Skip entirely in view mode —
  // the stored bill row's totals (sot_total_amount etc.) are the source of
  // truth there and must not be overwritten by a recompute off the item lines.
  useEffect(() => {
    if (!disabled && values?.data && values.data.length > 0) {
      // Recalculate totals whenever data changes
      const sot_total_gst = values.data.reduce(
        (total, item) => total + parseFloat(item?.item_tax_amount || 0),
        0
      );
      const sot_total_discount = values.data.reduce(
        (total, item) => total + parseFloat(item?.item_discount_amount || 0),
        0
      );
      const sot_sub_total = values.data.reduce(
        (subtotal, item) =>
          subtotal +
          parseFloat(item?.item_unit_price || 0) *
          parseFloat(item?.quantity || 0),
        0
      );
       const sot_total_without_inv_dis = values.data.reduce(
        (total, item) => total + parseFloat(item?.item_total_amount || 0),
        0
      );
 
      const sot_invoice_discount = values?.sot_is_invoice_dis_percentage
        ? (sot_total_without_inv_dis * parseFloat(values?.header_inv_dis_per || 0)) / 100
        : parseFloat(values?.header_inv_dis_per || 0);
 
      const sot_total_amount =
        sot_total_without_inv_dis - (parseFloat(sot_invoice_discount) || 0);

      const rounded_total = Math.round(sot_total_amount);
      const roundoff = (rounded_total - sot_total_amount).toFixed(2);

      // Update the totals
      setFieldValue("sot_total_gst", sot_total_gst.toFixed(2));
      setFieldValue("sot_total_discount", sot_total_discount.toFixed(2));
      setFieldValue("sot_sub_total", sot_sub_total.toFixed(2));
      setFieldValue("sot_total_amount", rounded_total.toFixed(2));
      setFieldValue("sot_round_off_value", roundoff);

      // Invoice profit %
      // Both the selling price (item_total_amount) and the purchase rate
      // (item_batch_final_purchase_rate) are tax-INCLUSIVE, so profit is the
      // straight difference between them — no GST is stripped from either side.
      // Step 1 (per-item): line purchase cost (incl. tax).
      //   item_batch_final_purchase_rate is the per-pack purchase rate.
      //   Mirrors the purchase calculation: split pack and loose quantities
      //   explicitly using pack_size, then sum:
      //     per_unit_cost  = item_batch_final_purchase_rate / pack_size
      //     pack_cost      = item_pack_qty × item_batch_final_purchase_rate
      //     loose_cost     = item_loose_qty × per_unit_cost
      //     line cost      = pack_cost + loose_cost
      //   Falls back to (rate / pack_size) × stored quantity when
      //   item_pack_qty / item_loose_qty are not set (e.g., edit mode).
      //   When rate is missing entirely (rows from getOrderById), recover
      //   from the backend margin %:
      //     line cost = item_total_amount × (1 − margin/100)
      // Step 2 (aggregate): the invoice-level discount reduces the (incl. tax)
      //   sale base, lowering both the profit and the base it is measured
      //   against — otherwise the % is overstated whenever a discount applies.
      //     base   = Σ item_total_amount − invoice_discount
      //     profit = base − Σ purchase_cost
      //     invoice_profit_percentage = profit / base × 100
      const sot_total_cost = values.data.reduce((total, item) => {
        const finalPurchaseRate = parseFloat(
          item?.item_batch_final_purchase_rate || 0
        );
        let lineCost;
        if (finalPurchaseRate > 0) {
          const pack_size = parseFloat(item?.item_pack_size) || 1;
          const pack_qty = parseFloat(item?.item_pack_qty || 0);
          const loose_qty = parseFloat(item?.item_loose_qty || 0);
          const effective_qty = pack_qty * pack_size + loose_qty;
          // Fall back to stored quantity when pack/loose not set (e.g., edit mode)
          const qty_for_cost = effective_qty > 0 ? effective_qty : parseFloat(item?.quantity || 0);
          lineCost = (finalPurchaseRate / pack_size) * qty_for_cost;
        } else {
          lineCost =
            parseFloat(item?.item_total_amount || 0) *
            (1 - parseFloat(item?.item_margin_percentage || 0) / 100);
        }
        return total + lineCost;
      }, 0);
      const profit_base =
        sot_total_without_inv_dis - (parseFloat(sot_invoice_discount) || 0);
      const sot_total_profit = profit_base - sot_total_cost;
      const invoice_profit_percentage =
        profit_base > 0 ? (sot_total_profit / profit_base) * 100 : 0;
      setFieldValue(
        "invoice_profit_percentage",
        invoice_profit_percentage.toFixed(2)
      );

      // Also recalculate invoice discount if it exists. Must be based on
      // sot_total_without_inv_dis (the pre-discount subtotal) — using
      // sot_total_amount (already net of this same discount) here would
      // apply the discount twice and show a smaller "Invoice Discount" than
      // what was actually subtracted to reach sot_total_amount above.
      if (values?.header_inv_dis_per && values?.sot_is_invoice_dis_percentage) {
        const invoiceDiscount = (sot_total_without_inv_dis * parseFloat(values.header_inv_dis_per)) / 100;
        setFieldValue("sot_invoice_discount", invoiceDiscount.toFixed(2));
        setFieldValue("sot_invoice_discount_percentage", values.header_inv_dis_per);
      } else if (values?.header_inv_dis_per && !values?.sot_is_invoice_dis_percentage) {
        const invoiceDiscount = parseFloat(values.header_inv_dis_per);
        const invoiceDiscountPercentage = sot_total_without_inv_dis > 0 ? (invoiceDiscount / sot_total_without_inv_dis) * 100 : 0;
        setFieldValue("sot_invoice_discount", invoiceDiscount.toFixed(2));
        setFieldValue("sot_invoice_discount_percentage", invoiceDiscountPercentage.toFixed(2));
      }
    }
  }, [values?.data, values?.header_inv_dis_per, values?.sot_is_invoice_dis_percentage, setFieldValue]);

  // Base a line's header discount is applied against: its amount after the
  // backend (item-level) discount, since the header discount compounds on top.
  const getRowHeaderBase = (row) => {
    const totalAmount =
      parseFloat(row?.quantity || 0) * parseFloat(row?.item_unit_price || 0);
    const backendDiscount =
      row?.item_backend_dis_per !== undefined &&
      row?.item_backend_dis_per !== null
        ? parseFloat(row.item_backend_dis_per || 0)
        : parseFloat(row?.item_discount_percentage || 0);
    return totalAmount * (1 - backendDiscount / 100);
  };
  // The user's min/max discount % permission, bounded to 0-100.
  const getDiscountPermissionBounds = () => {
    const parsedMinPer = parseFloat(user_discount?.min);
    const parsedMaxPer = parseFloat(user_discount?.max);
    return {
      minPer: Math.max(0, Number.isNaN(parsedMinPer) ? 0 : parsedMinPer),
      maxPer: Math.min(100, Number.isNaN(parsedMaxPer) ? 100 : parsedMaxPer),
    };
  };

  const onChangeHeaderDiscount = (
    discountPercentage,
    header_inv_dis_per,
    sot_is_invoice_dis_percentage,
    type = "",
    sot_is_header_dis_percentage = true
  ) => {
    let updatedData = [...values?.data];
    if (type === "header") {
      if (!Array.isArray(values.data) || isNaN(parseFloat(discountPercentage || 0)))
        return;

      // % mode: the same rate comes off every line item.
      // ₹ mode: the same flat amount comes off every line item - it is NOT
      // shared out across the bill (₹100 on a 2-item bill is ₹100 off each
      // item, ₹200 in total). It's expressed as that line's own equivalent %
      // so the compounding/tax math below is identical for both modes. A line
      // cheaper than the amount goes to zero, and because a flat amount is a
      // bigger % of a cheaper line, each line's % is held within the user's
      // min/max discount permission.
      const enteredHeaderDiscount = parseFloat(discountPercentage || 0);
      const { minPer, maxPer } = getDiscountPermissionBounds();
      const headerDiscountFor = (row) => {
        if (sot_is_header_dis_percentage) return enteredHeaderDiscount;
        const base = getRowHeaderBase(row);
        if (base <= 0 || enteredHeaderDiscount <= 0) return 0;
        const linePer = (Math.min(enteredHeaderDiscount, base) / base) * 100;
        return Math.min(maxPer, Math.max(minPer, linePer));
      };

      // The header discount each line takes. Capture the original backend
      // discount once, so we always know the "last discount rate from the
      // backend": on the first header-discount pass item_discount_percentage
      // still holds the backend value, so it is snapshotted into
      // item_backend_dis_per and reused thereafter. The header discount on
      // top of it may not take a line past its item's min / max discount.
      const linePlans = values.data.map((row) => {
        if (row?.is_promotional) return null;
        const backendDiscount =
          row?.item_backend_dis_per !== undefined &&
          row?.item_backend_dis_per !== null
            ? parseFloat(row.item_backend_dis_per || 0)
            : parseFloat(row.item_discount_percentage || 0);
        const limits = itemDiscountLimits(row);
        const { headerPer, adjusted } = headerDiscountWithinLimits(
          backendDiscount,
          headerDiscountFor(row),
          limits
        );
        return { backendDiscount, headerDiscount: headerPer, limits, adjusted };
      });
      const limitedLines = linePlans
        .map(
          (plan, index) =>
            plan?.adjusted && {
              name: values.data[index]?.item_name,
              limits: plan.limits,
              adjusted: plan.adjusted,
            }
        )
        .filter(Boolean);

      updatedData = values.data.map((row, index) => {
        const quantity = parseFloat(row.quantity || 0);
        const unitPrice = parseFloat(row.item_unit_price || 0);
        const totalAmount = quantity * unitPrice;

        // If row is a promotional item, skip applying header discount
        if (row?.is_promotional) return row;

        const { backendDiscount, headerDiscount } = linePlans[index];

        // Header discount is always applied ON TOP of the backend discount,
        // i.e. on the price that already has the backend discount applied
        // (compounded, not added to the percentage). When the header discount
        // is 0, the effective discount reverts to the backend discount rate.
        // Eg: 100 with 10% backend -> 90, then 10% header -> 90 - 9 = 81.
        const amountAfterBackend = totalAmount * (1 - backendDiscount / 100);
        const finalAmount = amountAfterBackend * (1 - headerDiscount / 100);
        const discount = totalAmount - finalAmount;
        const effectiveDiscount =
          totalAmount > 0 ? (discount / totalAmount) * 100 : 0;
        const taxPercentage = parseFloat(row.item_gst || 0);
        const tax = ((totalAmount - discount) * taxPercentage) / 100;

        const item_price_without_tax = (totalAmount - discount - tax).toFixed(
          2
        );
        const item_final_price = ((totalAmount - discount) / quantity).toFixed(
          2
        );

        return {
          ...row,
          item_backend_dis_per: backendDiscount,
          item_discount_percentage: effectiveDiscount,
          item_discount_amount: discount.toFixed(2),
          item_tax_amount: tax.toFixed(2),
          item_price_wiithout_tax: item_price_without_tax,
          item_sgst: (tax / 2).toFixed(2),
          item_cgst: (tax / 2).toFixed(2),
          item_igst: 0,
          item_final_price: item_final_price,
          item_total_amount: (totalAmount - discount).toFixed(2),
        };
      });

      setFieldValue("data", updatedData);
      if (limitedLines.length) {
        const message = headerLimitMessage(limitedLines);
        // Enter applies the discount and its blur applies it again — one toast
        errorToast(message, { toastId: message });
      }
    }

    // Update totals
    const sot_total_gst = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_tax_amount || 0),
      0
    );
    const sot_total_discount = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_discount_amount || 0),
      0
    );
    const sot_sub_total = updatedData.reduce(
      (subtotal, item) =>
        subtotal +
        parseFloat(item?.item_unit_price || 0) *
        parseFloat(item?.quantity || 0),
      0
    );

    const sot_total_without_inv_dis = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_total_amount || 0),
      0
    );

    const sot_invoice_discount = sot_is_invoice_dis_percentage
      ? (sot_total_without_inv_dis * header_inv_dis_per) / 100
      : header_inv_dis_per;

    const sot_invoice_discount_percentage = sot_is_invoice_dis_percentage
      ? header_inv_dis_per
      : (header_inv_dis_per / sot_total_without_inv_dis) * 100;

    const sot_total_amount =
      updatedData.reduce(
        (total, item) => total + parseFloat(item.item_total_amount || 0),
        0
      ) - parseFloat(sot_invoice_discount);
    const rounded_total = Math.round(sot_total_amount);
    const roundoff = (rounded_total - sot_total_amount).toFixed(2);
    setFieldValue("sot_total_gst", sot_total_gst.toFixed(2));
    setFieldValue("sot_total_discount", sot_total_discount.toFixed(2));
    setFieldValue("sot_sub_total", sot_sub_total.toFixed(2));
    setFieldValue("sot_total_amount", rounded_total.toFixed(2));
    setFieldValue("sot_round_off_value", roundoff);
    setFieldValue("header_dis_per", "");
    setFieldValue("sot_invoice_discount", sot_invoice_discount?.toFixed(2));
    setFieldValue(
      "sot_invoice_discount_percentage",
      sot_invoice_discount_percentage
    );
    // sot_is_invoice_dis_percentage (the %/₹ mode for header_inv_dis_per) is
    // intentionally NOT touched here — it must only change via the explicit
    // toggle button (and the "Header Disc %" field's own handler below), not
    // be inferred from whether a discount value happens to be non-zero.
    // Doing so previously fought with the toggle button (which always ended
    // up forced back to ₹ mode right after) and with typing a ₹ amount
    // (which flipped the mode to % immediately after applying it correctly).
  };
  // Clamps a raw header_inv_dis_per input to the allowed range and returns
  // the clamped value. Percentage mode is bounded to [0, 100] (further
  // restricted by the user's own min/max discount % permission, when set).
  // Amount (₹) mode is bounded to [₹0, bill total] — derived from the same
  // percentage bounds applied against sot_total_without_inv_dis — so a min/
  // max discount % permission of, say, 5–20% caps ₹ entry to 5–20% of the
  // bill total rather than the full total.
  const getClampedInvoiceDiscount = (val, showToast) => {
    const sot_total_without_inv_dis =
      parseFloat(
        values?.data?.reduce(
          (total, item) => total + parseFloat(item.item_total_amount || 0),
          0
        )
      ) || 0;

   const parsedMinPer = parseFloat(user_discount?.min);
    const parsedMaxPer = parseFloat(user_discount?.max);
    const minPer = Math.max(0, Number.isNaN(parsedMinPer) ? 0 : parsedMinPer);
    const maxPer = Math.min(100, Number.isNaN(parsedMaxPer) ? 100 : parsedMaxPer);

    if (values?.sot_is_invoice_dis_percentage) {
      if (val < minPer) {
        if (showToast) {
          errorToast(
            `Invoice discount cannot be less than ${minPer}%. Setting to minimum.`
          );
        }
        val = minPer;
      } else if (val > maxPer) {
        if (showToast) {
          errorToast(
            `Invoice discount cannot be more than ${maxPer}%. Setting to maximum.`
          );
        }
        val = maxPer;
      }
    } else {
      const minVal = (minPer / 100) * sot_total_without_inv_dis;
      const maxVal = (maxPer / 100) * sot_total_without_inv_dis;
      if (val < minVal) {
        if (showToast) {
          errorToast(
            `Invoice discount cannot be less than ${minPer}% (₹${minVal.toFixed(
              2
            )}) of subtotal. Setting to minimum.`
          );
        }
        val = minVal;
      } else if (val > maxVal) {
        if (showToast) {
          errorToast(
            `Invoice discount cannot be more than ${maxPer}% (₹${maxVal.toFixed(
              2
            )}) of subtotal. Setting to maximum.`
          );
        }
        val = maxVal;
      }
    }
    return val;
  };
  // Clamps a raw header_dis_per input the same way getClampedInvoiceDiscount
  // clamps header_inv_dis_per — percentage mode bounded to [0, 100] (further
  // restricted by the user's min/max discount % permission, when set), ₹
  // mode bounded to [₹0, pre-header-discount bill total]. Bill total here is
  // the sum AFTER backend per-item discount but BEFORE header discount, i.e.
  // the same base onChangeHeaderDiscount applies the header % against.
  const getClampedHeaderDiscount = (val, showToast) => {
    // ₹ mode is a flat amount off each line, so its rupee bounds come from
    // the lines themselves: the minimum permission measured on the cheapest
    // line, the maximum on the most expensive one (no line can drop further).
    // onChangeHeaderDiscount additionally holds each line within permission.
    const lineBases = (Array.isArray(values?.data) ? values.data : [])
      .filter((row) => !row?.is_promotional)
      .map(getRowHeaderBase)
      .filter((base) => base > 0);
    const smallestLine = lineBases.length ? Math.min(...lineBases) : 0;
    const largestLine = lineBases.length ? Math.max(...lineBases) : 0;

    const { minPer, maxPer } = getDiscountPermissionBounds();

    if (values?.sot_is_header_dis_percentage) {
      if (val < minPer) {
        if (showToast) {
          errorToast(
            `Header discount cannot be less than ${minPer}%. Setting to minimum.`
          );
        }
        val = minPer;
      } else if (val > maxPer) {
        if (showToast) {
          errorToast(
            `Header discount cannot be more than ${maxPer}%. Setting to maximum.`
          );
        }
        val = maxPer;
      }
    } else {
      const minVal = (minPer / 100) * smallestLine;
      const maxVal = (maxPer / 100) * largestLine;
      if (val < minVal) {
        if (showToast) {
          errorToast(
            `Header discount cannot be less than ${minPer}% (₹${minVal.toFixed(
              2
            )}) per item. Setting to minimum.`
          );
        }
        val = minVal;
      } else if (val > maxVal) {
        if (showToast) {
          errorToast(
            `Header discount cannot be more than ${maxPer}% (₹${maxVal.toFixed(
              2
            )}) per item. Setting to maximum.`
          );
        }
        val = maxVal;
      }
    }
    return val;
  };

  const onDoctorSuccess = (data) => {
    console.log("doctorrrr", data);
    const options = [
      ...data.data.DoctorList.map((item) => {
        const parts = [
          `Dr ${item?.dr_name}`, // name is always there
          item?.dr_hospital || null,
          item?.dr_area || null,
          item?.dr_city || null,
        ].filter(Boolean); // remove null/undefined/empty

        return {
          value: item.dr_name, // Store only the doctor name
          label: parts.join(" - "), // Display formatted name
          id: item.dr_id,
        };
      }),
    ];
    console.log("updated doctor", options?.[0]);
    setFieldValue("doctor_name", options?.[0]);
  };
  const onDoctorError = (data) => {
    console.log("doctor--", data);
  };
  const { initialValues } = useFormikContext();
  console.log("ini", initialValues);

  const didRun = useRef(false);
  // useEffect(() => {
  //   if (!initialValues?.doctor_name) return;
  //   console.log("doctor-initial", initialValues.doctor_name);

  //   // if (!didRun.current) {
  //   dispatch(
  //     doctorTableAction(
  //       {
  //         pageNumber: 1,
  //         pageSize: 20,
  //         filterData: {
  //           inputValue: initialValues.doctor_name,
  //           selectColumn: "dr_name",
  //           sort_by: "",
  //           sort_order: "",
  //         },
  //       },
  //       onDoctorSuccess,
  //       onDoctorError
  //     )
  //   );
  //   // didRun.current = true;
  //   // }
  // }, [initialValues?.doctor_name]);
  useEffect(() => {
    if (!initialValues?.doctor_id) return;
    console.log("doctor-initial-id", initialValues.doctor_id);

    // if (!didRun.current) {
    dispatch(
      doctorTableAction(
        {
          pageNumber: 1,
          pageSize: 20,
          filterData: {
            inputValue: initialValues.doctor_id,
            selectColumn: "dr_id",
            sort_by: "",
            sort_order: "",
          },
        },
        onDoctorSuccess,
        onDoctorError
      )
    );
    // didRun.current = true;
    // }
  }, [initialValues?.doctor_id]);

  useEffect(() => {
    return () => {
      const nextPath = window.location.hash; // Use hash for hash-based routing
      if (
        formValuesRef?.current &&
        !formValuesRef?.current?.submittedButton &&
        !disabled
      ) {
        handleBilling({
          ...formValuesRef.current,
          submittedButton: "draft",
          save: "auto",
        });
      }
    };
  }, [navigate]);

  const loadOptions = (inputValue) => {
    return new Promise((resolve) => {
      const onSuccess = (data) => {
        const options = [
          ...data.data.DoctorList.map((item) => {
            const parts = [
              `Dr ${item?.dr_name}`, // name is always there
              item?.dr_hospital || null,
              item?.dr_area || null,
              item?.dr_city || null,
            ].filter(Boolean); // remove null/undefined/empty

            return {
              value: item.dr_name, // Store only the doctor name
              label: parts.join(" - "), // Display formatted name
              id: item.dr_id,
            };
          }),
          {
            value: { text: "new_doctor", number: inputValue },
            label: <span className="text-primary">+ New Doctor</span>,
          },
        ];
        setLocalData(data?.data?.DoctorList || []);
        resolve(options);
      };

      const onError = (error) => {
        setLocalData([]);
        resolve([
          {
            value: { text: "new_doctor", number: inputValue },
            label: <span className="text-primary">+ New Doctor</span>,
          },
        ]);
      };

      if (inputValue?.length > 0) {
        console.log("...", values, inputValue);
        dispatch(
          doctorTableAction(
            {
              pageNumber: 1,
              pageSize: 20,
              filterData: {
                inputValue: inputValue,
                selectColumn: "dr_name",
                sort_by: "",
                sort_order: "",
              },
            },
            onSuccess,
            onError
          )
        );
      } else {
        resolve([]);
      }
    });
  };
  // const scheduledDrugValidation = (values) => {
  //   console.log("......", values);
  //   let hasError = false;
  //   let errorMessages = [];

  //   const ordinalSuffixes = ["st", "nd", "rd"];
  //   let scheduledList = [];
  //   values?.data?.forEach((item, index) => {
  //     if (
  //       item.item_schedule &&
  //       (item?.item_schedule !== "" ||
  //         item?.item_schedule !== null ||
  //         item?.item_schedule !== undefined)
  //     ) {
  //       scheduledList.push({
  //         itemNumber: index + 1,
  //         itemName: item.item_name,
  //       });
  //     }
  //   });
  //   console.log(
  //     "###############",
  //     scheduledList.length > 0,
  //     values?.sot_prescription === "" ||
  //       values?.sot_prescription === null ||
  //       values?.sot_prescription === undefined,
  //     scheduledList.length > 0 &&
  //       (values?.sot_prescription === "" ||
  //         values?.sot_prescription === null ||
  //         values?.sot_prescription === undefined)
  //   );
  //   if (
  //     scheduledList.length > 0 &&
  //     (values?.sot_prescription === "" ||
  //       values?.sot_prescription === null ||
  //       values?.sot_prescription === undefined)
  //   ) {
  //     console.log("values.sot_prescription", values.sot_prescription);
  //     hasError = true;
  //     errorToast(
  //       `Prescripction is required for ${scheduledList
  //         .map((item, index) => item?.itemName)
  //         .join(",")}`
  //     );
  //   }
  //   return hasError;
  // };
  const handleDraft = (selectedOption) => {
    console.log(values, "fgfdgdfgfdgdfgfdgfdg");

    let actionType = invoiceDraftCreateAction;
    // if (values.submittedButton === "draft") {
    // actionType = invoiceDraftCreateAction;

    if (page === "edit") {
      actionType = invoiceEditAction;
    }
    const filteredData = values?.data;
    if (
      filteredData.some(
        (item) => parseFloat(item?.item_margin_percentage) < 0
      ) &&
      values?.submittedButton !== "draft"
    ) {
      errorToast("Margin is negative, please check");
    } else {
      if (values?.data?.length > 0) {
        setLoading(true);
        setTimeout(() => {
          dispatch(
            actionType(
              {
                orderData: {
                  is_draft_order:
                    values.submittedButton === "draft" ? true : false,
                  store_id: values?.store_id || storeId || "",
                  cmr_id: values?.cmr_id || "",
                  cmr_phone_number: parseInt(values?.phone?.label),
                  sot_total_gst: values?.sot_total_gst || 0,
                  sot_total_discount: values?.sot_total_discount || 0,
                  sot_payment_status:
                    values.submittedButton === "draft" ? "pending" : "paid",
                  sot_transaction_id: "",
                  sot_billing_address: "",
                  sot_remarks: values?.sot_remarks,
                  sot_sub_total: values?.sot_sub_total || 0,
                  sot_total_amount: values?.sot_total_amount || 0,
                  sot_round_off_value: values?.sot_round_off_value || 0,
                  doctor_name: values?.doctor_name?.value,
                  sot_is_invoice_dis_percentage:
                    parseFloat(values?.header_inv_dis_per || 0) > 0
                      ? values?.sot_is_invoice_dis_percentage
                      : false,
                  sot_invoice_discount: values?.sot_invoice_discount,
                  sot_invoice_discount_percentage:
                    values?.sot_invoice_discount_percentage,
                  invoice_profit_percentage:
                    values?.invoice_profit_percentage || 0,
                  sot_prescription: values?.sot_prescription,
                  ...(values?.sot_id ? { sot_id: values?.sot_id } : {}),
                  sot_is_store_pick_up: values?.sot_is_store_pick_up,
                  sot_custom_invoice_date:
                    values?.sot_custom_invoice_date || null,
                  sot_custom_invoice_number:
                    values?.sot_custom_invoice_number || "",
                  sot_pay_to_address: values.sot_pay_to_address,
                  sot_ship_to_address: values.sot_ship_to_address,
                },
                paymentData: values?.paymentData?.map((pay) => ({
                  payment_mode_name: pay?.payment_mode_name,
                  payment_amount: pay?.payment_amount,
                  transaction_id: pay?.transaction_id || "",
                })),
                itemData: values?.data?.map((item, index) => ({
                  item_code: item?.item_code ?? "",
                  item_generic_name: item?.item_generic_name ?? "",
                  item_name: item?.item_name ?? "",
                  item_quantity: parseInt(item?.quantity),
                  item_pack_qty: item?.item_pack_qty || 0,
                  item_loose_qty: item?.item_loose_qty || 0,
                  //alter likith
                  item_free_quantity:
                    parseInt(item?.item_free_quantity ?? 0, 10) || 0,
                  item_uom: item?.item_uom ?? "",
                  item_batch_number: item?.item_batch_number ?? "",
                  item_exp_date: moment(item?.item_exp_date).format(
                    "YYYY-MM-DD"
                  ),
                  item_rack_location: item?.item_rack_location ?? "",
                  item_schedule: item?.item_schedule ?? "",
                  item_hsn: item?.item_hsn ?? "",
                  item_unit_price: parseFloat(item?.item_unit_price),
                  item_price_wiithout_tax: parseFloat(
                    item?.item_price_wiithout_tax
                  ),
                  item_discount_amount: parseFloat(item?.item_discount_amount),
                  item_discount_percentage:
                    parseFloat(item?.item_discount_percentage) || 0,
                  item_tax_amount: parseFloat(item?.item_tax_amount),
                  item_total_amount: parseFloat(
                    parseFloat(item.item_unit_price) * parseInt(item.quantity) -
                    parseFloat(item.item_discount_amount)
                  ).toFixed(2),
                  item_selling_price: parseFloat(item?.item_selling_price || 0).toFixed(2),
                  item_id: item?.item_id,
                  item_gst: parseFloat(item?.item_gst),
                  item_sgst: parseFloat(item?.item_sgst||0),
                  item_cgst: parseFloat(item?.item_cgst||0),
                  item_total_tax_percentage: parseFloat(
                    item?.item_total_tax_percentage
                  ),
                  item_pack_price: item?.item_pack_price || 0,
                  // item_manufacturer_id: item?.item_manufacturer_id ?? "",
                  // item_manufacturer_name: item?.item_manufacturer_name ?? "",
                })),
              },
              (data) => {
                setLoading(false);
                // resetForm();
                // setPaymentPanel(false);
                // if (values.submittedButton === "print") {
                //   handlea4Print(
                //     data?.data?.orderData?.[0]?.sot_id,
                //     "Billing",
                //     dispatch
                //   );

                //Needed for unwrap
                // handlePrint(data?.data, storeDetails);
                // }
                successToast(data?.message);
                setLoading(true);
                setTimeout(() => {
                  setLoading(false);
                }, 50);

                // Draft is saved, so the leave guard shouldn't ask about it
                allowBillingNavigation();
                navigate(
                  `/add-doctor?doctor=${selectedOption?.value?.number?.toString() || ""
                  }&draft=${data?.data?.orderData?.[0]?.sot_id}`
                );

                formValuesRef.current = null; // Reset after handling
              },
              (data) => {
                errorToast(data?.data?.message);
              }
            )
          );
        }, 500); // Timeout set to 100ms
      }
    }
  };
  // Benefit deductions shown in the totals breakdown — the same denorm
  // fields the save path ships (sot_scheme_discount / sot_coupon_discount /
  // sot_points_discount). Create/edit: the apply panels write them live;
  // view: they're hydrated from the stored bill row.
  const schemeDiscount = parseFloat(values?.sot_scheme_discount) || 0;
  const couponDiscount = parseFloat(values?.sot_coupon_discount) || 0;
  const pointsDiscount = parseFloat(values?.sot_points_discount) || 0;
  const combinedBenefitDiscount = parseFloat(values?.benifit_discount) || 0;
  const benefitSum =
    schemeDiscount + couponDiscount + pointsDiscount || combinedBenefitDiscount;
  const pointsRedeemed =
    Number(values?.sot_points_redeemed || values?.pointsDiscount?.points) || 0;
  return (
    <div className="h-[8rem] bg-white relative z-50">
      <LoadingOverlay isLoading={submitLoading || loading} />
      <BillingUploadPrescription
        open={uploadPrescription || uploadPrescriptionView}
        setOpen={
          uploadPrescriptionView
            ? setUploadPrescriptionView
            : setUploadPrescription
        }
        isView={uploadPrescriptionView}
      />
      {console.log("view====", values)}
      <div className="h-[4rem] border-y border-[var(--create-table-footer-border)] flex items-center justify-between">
        <div className="flex w-[80%] h-full">
          {billCreateInputs?.find((inp) => inp?.value === "remarks")
            ?.enabled && (
              <div className="w-[25%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex items-center">
                <ItemsInputField
                  disabled={false}
                  name={`sot_remarks`}
                  isLabelReq={false}
                  allowAll={true}
                  placeholder="Enter your remarks here..."
                />
              </div>
            )}

          {(billCreateInputs?.find((inp) => inp?.value === "doctor")?.enabled ||
            billCreateInputs?.find((inp) => inp?.value === "sot_prescription")
              ?.enabled) && (
              <div className="w-[50%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex items-center gap-3 justify-between">
                {billCreateInputs?.find((inp) => inp?.value === "doctor")
                  ?.enabled && (
                    <div className="w-[50%]">
                      <TextSearchField
                        isLabelReq={false}
                        isRequired={false}
                        disabled={disabled || page === "view"}
                        maxlength={50} // Changed from 10 to be more reasonable for names
                        label="Doctor"
                        className="z-20"
                        isValidNewOption={true}
                        // autoFocus={true}
                        name="doctor_name"
                        placeholder="Enter Doctor Name"
                        entityType="Doctor"
                        addNewRoute="/add-doctor"
                        loadOptions={loadOptions}
                        onChange={(selectedOption) => {
                          if (
                            selectedOption === "new_doctor" ||
                            selectedOption?.value?.text === "new_doctor"
                          ) {
                            if (values.cmr_id === "") {
                              errorToast("select customer before creating Doctor");
                            } else if (values.data.length === 0) {
                              errorToast("Add product before creating Doctor");
                            } else if (values.sot_custom_invoice_date === null) {
                              errorToast(
                                "Select invoice data before creating Doctor"
                              );
                            } else {
                              handleDraft(selectedOption);
                              // navigate(
                              //   `/add-doctor?doctor=${
                              //     selectedOption?.value?.number?.toString() || ""
                              //   }`
                              // );
                            }
                          } else {
                            console.log("selectedOption", selectedOption);
                            setFieldValue("doctor_name", selectedOption); // Set the selected doctor

                            // Find the doctor data using the correct ID field
                            const doctorData = localData?.find(
                              (item) => item?.dr_id === selectedOption?.value // Changed from cmr_id to dr_id
                            );

                            console.log("Selected doctor data:", doctorData); // Debug log

                            if (
                              searchRef &&
                              searchRef.current &&
                              selectedOption?.value
                            ) {
                              searchRef.current.focus();
                            }
                          }
                        }}
                        onCreateOption={() => { }}
                      />
                    </div>
                  )}
                {billCreateInputs?.find(
                  (inp) => inp?.value === "sot_prescription"
                )?.enabled && (
                    <>
                      <div className="w-[50%]">
                        <AddButton
                          isDisabled={disabled}
                          onclkFn={() => {
                            console.log("check my values", values);
                            setUploadPrescription(true);
                          }}
                          icon={<UploadIcon fill="var(--upload-button-fg)" />}
                          prefixIcon={true}
                          text={
                            values?.sot_prescription?.length > 0 && values.sot_prescription[0] !== ""
                              ? "Re-Upload Prescription"
                              : "Upload Prescription"
                          }
                          className="
                            flex items-center px-[10px] h-[44px] w-full whitespace-nowrap rounded-md gap-[8px]
                            bg-[var(--upload-button-bg)] cursor-pointer
                            text-[var(--upload-button-fg)]
                            border border-[var(--upload-button-border)]
                            text-[length:var(--upload-button-fs)] 
                            font-[var(--upload-button-fw)]
                        "
                        />
                      </div>
                      {values?.sot_prescription?.length > 0 && values.sot_prescription[0] !== "" && (
                        <div className="w-[10%]">
                          <AddButton
                            onclkFn={() => {
                              setUploadPrescriptionView(true);
                            }}
                            icon={<ViewIcon fill="var(--view-button-fg)" />}
                            prefixIcon={true}
                            className={`flex items-center justify-center  h-[44px] w-[3rem] whitespace-nowrap rounded-md gap-[8px]
           bg-[var(--view-button-bg)] ${values?.sot_prescription?.length > 0 && values.sot_prescription[0] !== "" ? "cursor-pointer" : "cursor-not-allowed"
                              } 
           text-[var(--view-button-fg)]
           border border-[var(--view-button-border)]`}
                          />
                        </div>
                      )}
                    </>
                  )}
              </div>
            )}
          {(billCreateInputs?.find((inp) => inp?.value === "header_dis_per")
            ?.enabled ||
            billCreateInputs?.find((inp) => inp?.value === "header_inv_dis_per")
              ?.enabled) && (
              <div className="w-[25%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex gap-3 items-center">
                {page !== "view" &&
                  billCreateInputs?.find((inp) => inp?.value === "header_dis_per")
                    ?.enabled && (
                    <div className="w-[50%] relative">
                      <ItemsInputField
                        type="number"
                        disabled={disabled}
                        name={`header_dis_per`}
                        isLabelReq={false}
                        placeholder={`Header Disc ${values?.sot_is_header_dis_percentage ? "%" : "₹"
                          }`}
                        onKeyDown={(event) => {
                          if (event.key === "Tab" || event.key === "Enter") {
                            const raw = parseFloat(event.target.value || 0);
                            const val = getClampedHeaderDiscount(raw, true);
                            if (val !== raw) {
                              setFieldValue("header_dis_per", val);
                            }
                            onChangeHeaderDiscount(
                              val,
                              parseFloat(values?.header_inv_dis_per || 0),
                              values?.sot_is_invoice_dis_percentage,
                              "header",
                              values?.sot_is_header_dis_percentage
                            );
                            // Only Enter jumps straight to the Inv Disc input
                            // (fast keyboard-entry flow). Tab must be left to
                            // the browser's native tab order so it stops on
                            // this field's own %/₹ toggle button first, then
                            // the Inv Disc input, then that field's toggle —
                            // jumping focus here on Tab too skipped both.
                            if (event.key === "Enter") {
                              header_inv_dis_per_ref?.current.focus();
                            }
                          }
                        }}
                        onBlur={(event) => {
                          // The Tab/Enter handler above applies the discount and
                          // then clears this box. On Enter the focus jump happens
                          // inside that handler, so blur still sees the typed
                          // value; on Tab the native focus move runs only after
                          // React has flushed the clear, so blur used to read an
                          // empty box, re-apply the discount as 0 and undo it.
                          // An empty box means "nothing to apply" — leave the
                          // discount as it stands. Type 0 to clear it.
                          if (`${event.target.value}`.trim() === "") return;
                          const raw = parseFloat(event.target.value || 0);
                          const val = getClampedHeaderDiscount(raw, false);
                          if (val !== raw) {
                            setFieldValue("header_dis_per", val);
                          }
                          onChangeHeaderDiscount(
                            val,
                            parseFloat(values?.header_inv_dis_per || 0),
                            values?.sot_is_invoice_dis_percentage,
                            "header",
                            values?.sot_is_header_dis_percentage
                          );
                        }}
                      />
                      <button
                        disabled={disabled}
                        onClick={() => {
                          setFieldValue(
                            "sot_is_header_dis_percentage",
                            !values?.sot_is_header_dis_percentage
                          );
                          setFieldValue("header_dis_per", "");
                          onChangeHeaderDiscount(
                            0,
                            parseFloat(values?.header_inv_dis_per || 0),
                            values?.sot_is_invoice_dis_percentage,
                            "header",
                            !values?.sot_is_header_dis_percentage
                          );
                        }}
                        type="button"
                        className="absolute top-1/2 -translate-y-1/2 right-0 w-[40px] min-h-[40px] flex items-center justify-center bg-[var(--create-tab-foot-subTot-bg)] border border-[var(--items-search-input-border)] rounded-r"
                      >
                        {values?.sot_is_header_dis_percentage ? "%" : "₹"}
                      </button>
                    </div>
                  )}
                {billCreateInputs?.find(
                  (inp) => inp?.value === "header_inv_dis_per"
                )?.enabled && (
                    <div
                      className={`${page === "view" ? "w-[100%]" : "w-[50%]"
                        }  relative`}
                    >
                      {isEditingDiscount ? (
                        <DiscountValueInput
                          value={invoiceDiscountEdit?.value}
                          isPercentage={!!invoiceDiscountEdit?.isPercentage}
                          onValueChange={(value) => setInvoiceDiscountEdit({ value })}
                          onToggle={() =>
                            setInvoiceDiscountEdit({
                              isPercentage: !invoiceDiscountEdit?.isPercentage,
                              value: "",
                            })
                          }
                        />
                      ) : (
                      <>
                      <ItemsInputField
                        type="number"
                        ref={header_inv_dis_per_ref}
                        disabled={disabled || values?.data?.length === 0}
                        name={`header_inv_dis_per`}
                        isLabelReq={false}
                        placeholder={`Inv Disc ${values?.sot_is_invoice_dis_percentage ? "%" : "₹"
                          }`}
                        onKeyDown={(event) => {
                          if (event.key === "Tab" || event.key === "Enter") {
                            const raw = parseFloat(event.target.value || 0);
                            const val = getClampedInvoiceDiscount(raw, true);
                            if (val !== raw) {
                              setFieldValue("header_inv_dis_per", val);
                            }
                            onChangeHeaderDiscount(
                              parseFloat(values?.header_dis_per || 0),
                              val,
                              values?.sot_is_invoice_dis_percentage
                            );
                          }
                        }}
                        onBlur={(event) => {
                          const raw = parseFloat(event.target.value || 0);
                          const val = getClampedInvoiceDiscount(raw, false);
                          if (val !== raw) {
                            setFieldValue("header_inv_dis_per", val);
                          }
                          onChangeHeaderDiscount(
                            parseFloat(values?.header_dis_per || 0),
                            val,
                            values?.sot_is_invoice_dis_percentage
                          );
                        }}
                      />
                      <button
                        disabled={disabled}
                        onClick={() => {
                          setFieldValue(
                            "sot_is_invoice_dis_percentage",
                            !values?.sot_is_invoice_dis_percentage
                          );
                          setFieldValue("header_inv_dis_per", "");
                          onChangeHeaderDiscount(
                            parseFloat(values?.header_dis_per),
                            // parseFloat(values?.header_inv_dis_per),
                            0,
                            !values?.sot_is_invoice_dis_percentage
                          );
                        }}
                        type="button"
                        className="absolute top-1/2 -translate-y-1/2 right-0 w-[40px] min-h-[40px] flex items-center justify-center bg-[var(--create-tab-foot-subTot-bg)] border border-[var(--items-search-input-border)] rounded-r"
                      >
                        {values?.sot_is_invoice_dis_percentage ? "%" : "₹"}
                      </button>
                      </>
                      )}
                    </div>
                  )}
              </div>
            )}
        </div>

        <div
          className="justify-between w-[20%] px-5 h-full flex flex-col items-center
      bg-[var(--create-table-footer-totals-bg)] text-[var(--create-table-footer-totals-fg)]
      text-[length:var(--create-table-footer-totals-fs)] font-[var(--create-table-footer-totals-fw)] relative"
        >
          {/* Toggle Button */}
          {!isOpen && (
            <div
              className="absolute top-[-1.2rem] left-1/2 transform -translate-x-1/2 bg-[var(--create-table-footer-totals-bg)] cursor-pointer 
                    flex items-start justify-center"
              style={{
                width: "80px",
                height: "55px",
                borderRadius: "9999px", // makes it oval
                padding: "0.2rem",
              }}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
          )}
          {isOpen && (
            <div
              className="p-5 flex flex-col w-full space-y-5 absolute bottom-[100%]"
              style={{
                backgroundColor: "var(--create-tab-foot-subTot-bg)",
                color: "var(--create-tab-foot-subTot-fg)",
                fontSize: "var(--create-tab-foot-subTot-fs)",
                fontWeight: "var(--create-tab-foot-subTot-fw)",
              }}
            >
              <div className="flex justify-between">
                <span>Sub Total</span>
                <span>₹{values?.sot_sub_total || "0.00"}</span>
              </div>
              <div className="flex justify-between">
                <span>Discount</span>
                <span style={{ color: "var(--success-color)" }}>
                  {"- "}₹{values?.sot_total_discount || "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Invoice Discount</span>
                <span style={{ color: "var(--success-color)" }}>
                  {"- "}₹{values?.sot_invoice_discount || "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Round off</span>
                <span
                  className={
                    parseFloat(values?.sot_round_off_value || 0) > 0
                      ? "text-[var(--error-color)]"
                      : parseFloat(values?.sot_round_off_value || 0) < 0
                        ? "text-[var(--success-color)]"
                        : ""
                  }
                >
                  ₹{parseFloat(values?.sot_round_off_value || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tax</span>
                <span style={{ color: "var(--error-color)" }}>
                  ₹{values?.sot_total_gst || "0.00"}
                </span>
              </div>
              {/* {schemeDiscount > 0 && (
                <div className="flex justify-between">
                  <span>Scheme</span>
                  <span style={{ color: "var(--success-color)" }}>
                    {"- "}₹{schemeDiscount.toFixed(2)}
                  </span>
                </div>
              )} */}
              {values?.benifit_discount > 0 && (
                <div className="flex justify-between">
                  <span>Benifit Disc</span>
                  <span style={{ color: "var(--success-color)" }}>
                    {"- "}₹{values?.benifit_discount.toFixed(2)}
                  </span>
                </div>
              )}
              {/* {pointsDiscount > 0 && (
                <div className="flex justify-between">
                  <span>
                    Loyalty points
                    {pointsRedeemed > 0 && ` · ${pointsRedeemed} pts`}
                  </span>
                  <span style={{ color: "var(--success-color)" }}>
                    {"- "}₹{pointsDiscount.toFixed(2)}
                  </span>
                </div>
              )} */}
              <div
                className={`absolute top-[-2.3rem] left-1/2 transform -translate-x-1/2 bg-[var(--create-tab-foot-subTot-bg)] cursor-pointer
              flex items-start justify-center `}
                style={{
                  width: "80px",
                  height: "55px",
                  borderRadius: "9999px", // makes it oval
                  padding: "0.2rem",
                  zIndex: "",
                }}
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </div>
            </div>
          )}

          {/* Total Amount */}
          <div className="flex justify-between w-full h-full items-center">
            <span>{isEditingDiscount ? "New Total" : "Total"}</span>
            <span>
              ₹{isEditingDiscount
                ? previewBillTotal(values).toFixed(2)
                : values?.sot_total_amount}
            </span>
          </div>
        </div>
      </div>

      <div className="h-[4rem] flex justify-between px-5">
        <div className="flex items-center text-sm text-[var(--error-color)]">
          {isEditingDiscount &&
            !values?.can_edit_item_discount &&
            "This bill has a return or a coupon / scheme / points discount, so only the invoice discount can be changed"}
        </div>
        <div className="flex gap-3 items-center">
          {!disabled && (
            <AddButton
              prefixIcon={false}
              isDisabled={submitLoading || loading}
              text={"Draft"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--draft-button-bg)] cursor-pointer
text-[var(--draft-button-fg)]
border border-[var(--draft-button-border)]
text-[length:var(--draft-button-fs)]
font-[var(--draft-button-fw)]
"
              type="submit"
              onclkFn={() => {
                if (submitLoading || loading) return;
                setFieldValue("submittedButton", "draft");
                if (Object.keys(errors).length > 0) {
                  handleShowErrorToast(errors);
                }
              }}
            />
          )}
          {console.log("::", values?.submittedButton)}
          {canEditDiscount && (
            <AddButton
              prefixIcon={false}
              isDisabled={savingDiscount}
              text={isEditingDiscount ? "Cancel" : "Edit Discount"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--draft-button-bg)] cursor-pointer
text-[var(--draft-button-fg)]
border border-[var(--draft-button-border)]
text-[length:var(--draft-button-fs)]
font-[var(--draft-button-fw)]
"
              type="button"
              onclkFn={() =>
                isEditingDiscount
                  ? setFieldValue("discount_edit", null)
                  : startDiscountEdit()
              }
            />
          )}
          {isEditingDiscount && (
            <AddButton
              prefixIcon={false}
              loading={savingDiscount}
              text={"Save Discount"}
              spinnerClass="h-4 w-4 border-t-2 border-[var(--primary-button-fg-color)]"
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-create-fs)]
font-[var(--primary-button-font-weight)]
"
              type="button"
              onclkFn={saveDiscountEdit}
            />
          )}
          <AddButton
            prefixIcon={false}
            isDisabled={!disabled && (submitLoading || loading)}
            text={disabled ? "View Payment Method" : "Proceed to Payment"}
            className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-create-fs)]
font-[var(--primary-button-font-weight)]
"
            type="submit"
            onclkFn={async () => {
              if (!disabled && (submitLoading || loading)) return;
              // const validate = scheduledDrugValidation(values);
              // console.log("!!!!", !validate);
              // if (!validate) {
              setFieldValue("submittedButton", "proceed");

              const formErrors = await validateForm();
              if (Object.keys(formErrors).length > 0) {
                handleShowErrorToast(formErrors);
              }
              // }
            }}
          />
          {disabled && values?.sot_order_status === "draft" && (
            <AddButton
              prefixIcon={false}
              text={"EDIT"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-font-size)] 
font-[var(--primary-button-font-weight)]
"
              type="button"
              onclkFn={() => {
                navigate(`/edit-bill/${values?.sot_id}`);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingFooterSection;
